import { useState, useCallback } from 'react';
import { LaunchesService } from '../services/launchesApi';
import type { LaunchBoardUnit } from '../types';

interface UseLaunchActionsReturn {
  loading: boolean;
  hold: (unitId: string, clientId: string) => Promise<LaunchBoardUnit>;
  selfRelease: (unitId: string) => Promise<LaunchBoardUnit>;
  confirmDeposit: (unitId: string) => Promise<LaunchBoardUnit>;
  markSold: (unitId: string) => Promise<LaunchBoardUnit>;
  forceRelease: (unitId: string) => Promise<LaunchBoardUnit>;
  revertSold: (unitId: string) => Promise<LaunchBoardUnit>;
  /** available -> deposit_pending, with the receipt the manager reviews. */
  submitDeposit: (unitId: string, clientId: string, proofKey: string) => Promise<LaunchBoardUnit>;
  validateDeposit: (unitId: string) => Promise<LaunchBoardUnit>;
  rejectDeposit: (unitId: string, reason: string) => Promise<LaunchBoardUnit>;
}

/**
 * Provides mutation callbacks for unit allocation actions on a given launch.
 */
export function useLaunchActions(agencyId: string, launchId: string): UseLaunchActionsReturn {
  const [loading, setLoading] = useState(false);

  const wrap = useCallback(
    <T,>(fn: () => Promise<T>): Promise<T> => {
      setLoading(true);
      return fn().finally(() => setLoading(false));
    },
    [],
  );

  const hold = useCallback(
    (unitId: string, clientId: string) => wrap(() => LaunchesService.hold(agencyId, launchId, unitId, clientId)),
    [agencyId, launchId, wrap],
  );

  const selfRelease = useCallback(
    (unitId: string) => wrap(() => LaunchesService.selfRelease(agencyId, launchId, unitId)),
    [agencyId, launchId, wrap],
  );

  const confirmDeposit = useCallback(
    (unitId: string) => wrap(() => LaunchesService.confirmDeposit(agencyId, launchId, unitId)),
    [agencyId, launchId, wrap],
  );

  const markSold = useCallback(
    (unitId: string) => wrap(() => LaunchesService.markSold(agencyId, launchId, unitId)),
    [agencyId, launchId, wrap],
  );

  const forceRelease = useCallback(
    (unitId: string) => wrap(() => LaunchesService.forceRelease(agencyId, launchId, unitId)),
    [agencyId, launchId, wrap],
  );

  const revertSold = useCallback(
    (unitId: string) => wrap(() => LaunchesService.revertSold(agencyId, launchId, unitId)),
    [agencyId, launchId, wrap],
  );


  const submitDeposit = useCallback(
    (unitId: string, clientId: string, proofKey: string) =>
      wrap(() => LaunchesService.submitDeposit(agencyId, launchId, unitId, {
        client_id: clientId, proof_key: proofKey,
      })),
    [agencyId, launchId, wrap],
  );

  const validateDeposit = useCallback(
    (unitId: string) => wrap(() => LaunchesService.validateDeposit(agencyId, launchId, unitId)),
    [agencyId, launchId, wrap],
  );

  const rejectDeposit = useCallback(
    (unitId: string, reason: string) =>
      wrap(() => LaunchesService.rejectDeposit(agencyId, launchId, unitId, reason)),
    [agencyId, launchId, wrap],
  );

  return {
    loading, hold, selfRelease, confirmDeposit, markSold, forceRelease, revertSold,
    submitDeposit, validateDeposit, rejectDeposit,
  };
}
